import { useState, useEffect, useCallback } from 'react';
import { getVehicleOpportunities } from '../../../../../services/opportunities';

// Helper function to check if body is empty
const isBodyEmpty = (body) => {
  if (!body) return true;
  if (Array.isArray(body)) return body.length === 0;
  if (typeof body === 'object') return Object.keys(body).length === 0;
  return false;
};

export const useVehicleOpportunities = (partyUuid, vehicleUuid, options = {}) => {
  const [opportunities, setOpportunities] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isEmpty, setIsEmpty] = useState(false);

  const fetchOpportunities = useCallback(async (params = {}) => {
    if (!partyUuid || !vehicleUuid) {
      return;
    }

    setLoading(true);
    setError(null);
    setIsEmpty(false);

    try {
      const response = await getVehicleOpportunities({
        party_uuid: partyUuid,
        vehicle_uuid: vehicleUuid,
        page: params.page || options.page || 1,
        page_size: params.page_size || options.pageSize || 20,
        order_by: params.order_by || options.orderBy || ["created_at"],
        order_direction: params.order_direction || options.orderDirection || ["DESC"],
        search: params.search || options.search,
      });

      const responseStatusCode = response?.statusCode;
      const isValidResponse = responseStatusCode === 200 || responseStatusCode === 201;

      if (!isValidResponse) {
        const errorMessage = response?.msgCode?.[0]?.description || 'Invalid response status code';
        throw new Error(errorMessage);
      }

      const body = response?.body;
      
      if (isBodyEmpty(body)) {
        setIsEmpty(true);
        setOpportunities([]);
        setMeta(response.meta || null);
      } else {
        setIsEmpty(false);
        setOpportunities(Array.isArray(body) ? body : []);
        setMeta(response.meta || null);
      }
    } catch (err) {
      console.error('Error fetching vehicle opportunities:', err);
      setError(err);
      setOpportunities([]);
      setMeta(null);
      setIsEmpty(false);
    } finally {
      setLoading(false);
    }
  }, [partyUuid, vehicleUuid]);

  useEffect(() => {
    if (partyUuid && vehicleUuid) {
      fetchOpportunities();
    }
  }, [partyUuid, vehicleUuid, fetchOpportunities]);

  return {
    opportunities,
    meta,
    loading,
    error,
    isEmpty,
    refetch: fetchOpportunities,
  };
};

